import { assign, cloneDeep, filter, find, forEach, isEmpty, isNil, join, map, unset } from 'lodash';

import * as connectorOps from '../api/Connectors/connector-operations';
import Connectors, { FILE_TYPES } from '../api/Connectors/Connectors';
import * as sourceOps from '../api/Sources/source-operations';
import Sources from '../api/Sources/Sources';
import * as nodeOps from '../api/Tasks/task-node-operations';
import * as taskOps from '../api/Tasks/task-operations';
import * as paramOps from '../api/Tasks/task-param-operations';

import Task from './TaskClass';

const EXTENSION_LIST = join(
  filter(
    map(FILE_TYPES.properties, ({ extension }) => extension),
    ext => !isEmpty(ext)
  ),
  '|'
);
const EXTENSION_REGEX = new RegExp(`^(.*\\.(?=(${EXTENSION_LIST})$))[^.]*$`, 'i');

/**
 * Clean and validate task list.
 *
 * @param {object} arg - The response object.
 * @param {Array} arg.tasklist - The task list to clean and validate.
 * @param {object} arg.schema - The schema to validate and clean the tasks.
 * @returns {Array} The cleaned and validated task list.
 * @throws {Error} Error if anything goes wrong.
 */
export const cleanTasks = ({ tasklist, schema }) => {
  try {
    return map(tasklist, task => {
      const taskToClean = cloneDeep(task);
      const cleanedTask = schema.clean(taskToClean);
      schema.validate(cleanedTask);
      return cleanedTask;
    });
  } catch (error) {
    console.error(error);
    throw new Error(error.message);
  }
};

/**
 * Checks if task nodes are ready.
 *
 * @param {object} selectedTask - The task to check.
 * @returns {object} The checked task.
 * @throws {Error} Error if anything goes wrong.
 */
export const checkTaskNodes = selectedTask => {
  const newSelectedTask = cloneDeep(selectedTask);
  const taskNodesToCheck = taskOps.getNodes(newSelectedTask);

  const newTaskNodes = map(taskNodesToCheck, node => {
    const checkedNode = cloneDeep(node);

    if (nodeOps.isConnection(checkedNode)) {
      const taskHelper = new Task(newSelectedTask);

      const isRoot = taskHelper.isRoot(checkedNode);
      const isLeaf = taskHelper.isLeaf(checkedNode);
      const isTemporal = nodeOps.isTemporal(checkedNode);

      if (!isRoot && !isLeaf && isTemporal) {
        checkedNode.isNodeReady = true;
        checkedNode.connectionParams.createMapServerElement = false;
        unset(checkedNode.connectionParams, [
          'autorefresh',
          'refreshLatency',
          'mapServerElementType'
        ]);
      }
    }

    if (nodeOps.isSubTask(checkedNode)) {
      let areAllParametersDefined = true;

      forEach(nodeOps.getParamList(checkedNode), parameter => {
        if (paramOps.isRequired(parameter) && isEmpty(paramOps.getValue(parameter))) {
          areAllParametersDefined = false;
          return false;
        }
        return true;
      });

      checkedNode.isNodeReady = areAllParametersDefined;
    }

    return checkedNode;
  });

  newSelectedTask.nodes = newTaskNodes;
  newSelectedTask.isTaskReadyToSend = taskOps.isTaskReady(newSelectedTask);

  return newSelectedTask;
};

/**
 * Gets the form values for the subtask parameters and builds the subtask node.
 *
 * @param {object} arg - The response object.
 * @param {object} arg.form - The form object.
 * @param {object} arg.selectedTask - The task to update.
 * @param {string} arg.selectedNodeId - The id of the node to update.
 * @returns {object} The task with the new values in the corresponding node.
 * @throws {Error} Error if anything goes wrong.
 */
export const subtaskParametersFormWorkflow = ({ form, selectedTask, selectedNodeId }) => {
  const newSelectedTask = cloneDeep(selectedTask);
  try {
    const newNode = find(newSelectedTask.nodes, ['id', selectedNodeId]);
    const paramList = nodeOps.getParamList(newNode);
    forEach(paramList, parameter => {
      const parameterToUpdate = parameter;
      if (paramOps.isBoolean(parameter)) {
        parameterToUpdate.value = form[parameter.id].checked;
      } else if (paramOps.isMultiValue(parameter)) {
        const selectedOptions = filter(form[parameter.id].options, option => option.selected);
        const selectedValues = map(selectedOptions, option => option.value);
        const filterEmpty = filter(selectedValues, value => !isEmpty(value));
        parameterToUpdate.value = filterEmpty;
        if (paramOps.isRequired(parameter) && isEmpty(filterEmpty)) throw new Error('empty value');
      } else {
        if (paramOps.isRequired(parameter)) {
          switch (true) {
            case paramOps.isNumber(parameter):
            case paramOps.isDate(parameter):
              if (form[parameter.id].value === '') throw new Error('empty value');
              break;
            default:
              if (isEmpty(form[parameter.id].value)) throw new Error('empty value');
              break;
          }
        }
        parameterToUpdate.value = form[parameter.id].value;
      }
    });

    newNode.isNodeReady = true;
    newSelectedTask.isTaskReadyToSend = taskOps.isTaskReady(newSelectedTask);

    return newSelectedTask;
  } catch (error) {
    throw new Error(error.message);
  }
};

/**
 * Gets the form values for the connection parameters and builds
 * the connection node.
 *
 * @param {object} arg - The response object.
 * @param {object} arg.form - The form object.
 * @param {object} arg.selectedTask - The task to update.
 * @param {string} arg.selectedNodeId - The id of the node to update.
 * @param {string} arg.selectedSourceId - The id of the node to update.
 * @returns {object} The task with the new values in the corresponding node.
 * @throws {Error} Error if anything goes wrong.
 */
export const connectionParametersFormWorkflow = ({
  form,
  selectedTask,
  selectedNodeId,
  selectedSourceId
}) => {
  const newSelectedTask = cloneDeep(selectedTask);
  try {
    const newNode = find(newSelectedTask.nodes, ['id', selectedNodeId]);

    const newSourceId = selectedSourceId;

    let sourceSelected = { _id: 'temporal' };
    let connectorForSource = { _id: 'temporal' };

    const taskHelper = new Task(newSelectedTask);
    const isRoot = taskHelper.isRoot(newNode);
    const isLeaf = taskHelper.isLeaf(newNode);

    const isMapServerElementAllowed = nodeOps.isMapServerElementAllowed(newNode);
    const canNodeCreateMapServerElement = !isRoot || (isRoot && isLeaf);
    const newConnParams = newNode.connectionParams;

    if (isRoot || isLeaf) newConnParams.temporal = false;
    else newConnParams.temporal = form.isTemporal.checked;

    if (!newConnParams.temporal) {
      // node is not temporal
      // we need to set source, connector, createMapServerElement,
      // mapServerElementType, autorefresh, refreshLatency

      if (isEmpty(newSourceId)) throw new Error('Select Source');

      // the subscription should be up already,
      // so there is no need to subscribe here
      sourceSelected = Sources.findOne(newSourceId);
      connectorForSource = Connectors.findOne(sourceOps.getConnectorId(sourceSelected));
      if (connectorOps.isOutput(connectorForSource)) {
        let layerName = sourceOps.getLayerName(sourceSelected);
        if (!EXTENSION_REGEX.test(layerName)) {
          const { extension } =
            find(FILE_TYPES.properties, ['value', connectorOps.getFileType(connectorForSource)]) ||
            {};
          layerName += isEmpty(extension) ? '' : `.${extension}`;
        }
        connectorForSource.filePath += `/${layerName}`;
      }
      if (isNil(sourceSelected) || isEmpty(sourceSelected))
        throw new Error('Cannot find selected source');

      if (isNil(connectorForSource) || isEmpty(connectorForSource))
        throw new Error('Cannot find connection for source');

      if (canNodeCreateMapServerElement && isMapServerElementAllowed) {
        newConnParams.createMapServerElement = form.createMapServerElement.checked;
        if (newConnParams.createMapServerElement) {
          newConnParams.mapServerElementType = form.mapServerElementType.value;
          newConnParams.autorefresh = form.autorefresh.checked;
          if (isEmpty(newConnParams.mapServerElementType)) throw new Error('Select Type');

          if (!newConnParams.autorefresh)
            newConnParams.refreshLatency = Number(form.refreshLatency.value);
          else unset(newConnParams, ['refreshLatency']);
        }
      }
    }

    if (
      newConnParams.temporal ||
      !canNodeCreateMapServerElement ||
      !isMapServerElementAllowed ||
      !newConnParams.createMapServerElement
    ) {
      newConnParams.createMapServerElement = false;
      unset(newConnParams, ['autorefresh', 'refreshLatency', 'mapServerElementType']);
    }

    assign(newNode.connectionParams, {
      ...newConnParams,
      connector: connectorForSource,
      source: sourceSelected
    });

    newNode.isNodeReady = true;
    newSelectedTask.isTaskReadyToSend = taskOps.isTaskReady(newSelectedTask);

    return newSelectedTask;
  } catch (error) {
    throw new Error(error.message);
  }
};

/**
 * Gets the list of map server elements created on the resulting tasklist.
 *
 * @param {Array} tasklist - The task list to clean and validate.
 * @returns {Array} The Map Server Elements Result for all the completed tasks.
 * @throws {Error} Error if anything goes wrong.
 */
export const getMapServerElementsFromTasklist = tasklist => {
  const resultingMapServerElements = [];
  forEach(tasklist, resultTask => {
    const taskHelper = new Task(resultTask);
    const connectionsList = taskHelper.getConnections();
    forEach(connectionsList, connection => {
      const connectionMapServerElements = map(
        nodeOps.getMapServerElementResults(connection),
        mapServerElement => {
          const newMSE = cloneDeep(mapServerElement);
          newMSE.sourceId = sourceOps.getId(nodeOps.getSource(connection));
          return newMSE;
        }
      );
      resultingMapServerElements.push(...connectionMapServerElements);
    });
  });
  return resultingMapServerElements;
};
